/**
 * Tool calls at stream end
 *
 * Works around a race in @neuro/chat-adapter (1.7.0). When the router
 * stream delivers a tool call, the adapter starts the tool and, when that
 * stream finishes, ends the turn unless a tool is still running. A tool
 * that answers at once — every ui_* tool runs in the browser — finishes
 * before the stream does, so the turn is ended and the model's follow-up
 * answer (sent under that turn) is dropped: the user saw the tool run and
 * then an empty reply.
 *
 * This wrapper holds tool-call deltas until the stream is done and hands
 * them over just before `done`, so the tool is still running when the
 * adapter decides whether to end the turn. Text and metadata deltas pass
 * straight through. Drop it once the adapter tracks "this stream asked for
 * a tool" instead of "a tool is still running".
 */
import type { ChatModelRouterClient, RouterDelta, RouterResponseStream } from '@neuro/chat-adapter';

export function deliverToolCallsAtStreamEnd(router: ChatModelRouterClient): ChatModelRouterClient {
  return {
    ...router,
    streamCompletion(req, signal): RouterResponseStream {
      const inner = router.streamCompletion(req, signal);
      const deltaHandlers: Array<(delta: RouterDelta) => void> = [];
      const errorHandlers: Array<(err: Error) => void> = [];
      const doneHandlers: Array<() => void> = [];
      const heldToolCalls: RouterDelta[] = [];

      inner.onDelta((delta) => {
        if (delta.toolCall) {
          heldToolCalls.push(delta);
          return;
        }
        for (const handler of deltaHandlers) handler(delta);
      });
      inner.onError((err) => {
        for (const handler of errorHandlers) handler(err);
      });
      inner.onDone(() => {
        for (const delta of heldToolCalls.splice(0)) {
          for (const handler of deltaHandlers) handler(delta);
        }
        for (const handler of doneHandlers) handler();
      });

      return {
        onDelta: (handler) => void deltaHandlers.push(handler),
        onError: (handler) => void errorHandlers.push(handler),
        onDone: (handler) => void doneHandlers.push(handler),
        abort: () => inner.abort(),
      };
    },
  };
}
